import { List, Record } from "immutable";
import { JSONSchema7 } from "json-schema";

export const OCC_CUSTOM_FORM_FIELD_TYPES = [
  "checkbox-multi-select",
  "currency",
  "date-time",
  "date",
  "email",
  "integer",
  "multi-line-text",
  "single-select",
  "text",
  "time",
] as const;

export type OCCCustomFormFieldType = (typeof OCC_CUSTOM_FORM_FIELD_TYPES)[number];

const convertArraysToLists = (obj: { [key: string]: unknown }): { [key: string]: unknown } => {
  const keys = Object.keys(obj) as (keyof typeof obj)[];

  for (const k of keys) {
    if (Array.isArray(obj[k])) {
      obj[k] = List(obj[k]);
      continue;
    }

    if (typeof obj[k] === "object" && obj[k] !== null && !List.isList(obj[k])) {
      obj[k] = convertArraysToLists(obj[k] as { [key: string]: unknown });
    }
  }

  return obj;
};

export const customFormSchemaFieldsFromJSON = (
  json: Partial<ICustomFormSchemaFieldsData>,
): CustomFormSchemaFieldsRecord => {
  const record: ICustomFormSchemaFields = { ...(convertArraysToLists(json) as any) };

  if (json.occ_type && !OCC_CUSTOM_FORM_FIELD_TYPES.includes(json.occ_type)) {
    record.occ_type = "text";
  }

  return new CustomFormSchemaFieldsRecord(record);
};

export const customFormSchemaFieldsToJSON = (
  record: CustomFormSchemaFieldsRecord,
): Partial<ICustomFormSchemaFieldsData> => {
  const json = record.toJS() as Partial<ICustomFormSchemaFieldsData>;

  const allKeys: (keyof ICustomFormSchemaFieldsData)[] = Object.keys(json);
  for (const key of allKeys) {
    if (json[key] !== undefined) {
      continue;
    }

    delete json[key];
  }

  return json;
};

export interface ICustomFormSchemaFieldsData extends Omit<JSONSchema7, "required"> {
  /** OCC-specific field name */
  fieldName?: string;
  /** OCC-specific field type */
  occ_type?: OCCCustomFormFieldType;
  /** OCC-specific field to indicate if the field is required */
  required?: boolean;

  // Preserves original functionality
  [name: string]: any;
}

export interface ICustomFormSchemaFields extends Omit<JSONSchema7, "required"> {
  /** OCC-specific field name */
  fieldName: string;
  /** OCC-specific field type */
  occ_type: OCCCustomFormFieldType;
  /** OCC-specific field to indicate if the field is required */
  required: boolean;

  // Preserves original functionality
  [name: string]: any;
}

export const defaultCustomFormSchemaFields: ICustomFormSchemaFields = {
  // OCC-specific fields
  fieldName: "",
  occ_type: "text",
  required: false,

  // JSONSchema7 fields
  $comment: undefined,
  $defs: undefined,
  $id: undefined,
  $ref: undefined,
  $schema: undefined,
  additionalItems: undefined,
  additionalProperties: undefined,
  allOf: undefined,
  anyOf: undefined,
  const: undefined,
  contains: undefined,
  contentEncoding: undefined,
  contentMediaType: undefined,
  default: undefined,
  definitions: undefined,
  dependencies: undefined,
  description: undefined,
  else: undefined,
  enum: undefined,
  examples: undefined,
  exclusiveMaximum: undefined,
  exclusiveMinimum: undefined,
  format: undefined,
  if: undefined,
  items: undefined,
  maximum: undefined,
  maxItems: undefined,
  maxLength: undefined,
  maxProperties: undefined,
  minimum: undefined,
  minItems: undefined,
  minLength: undefined,
  minProperties: undefined,
  multipleOf: undefined,
  not: undefined,
  oneOf: undefined,
  pattern: undefined,
  patternProperties: undefined,
  properties: undefined,
  propertyNames: undefined,
  readOnly: undefined,
  then: undefined,
  title: undefined,
  type: undefined,
  uniqueItems: undefined,
  writeOnly: undefined,
};

export class CustomFormSchemaFieldsRecord
  extends Record<ICustomFormSchemaFields>(defaultCustomFormSchemaFields)
  implements ICustomFormSchemaFields {}
